﻿import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import {
	CampaignRowData,
	M1CurrentSectionData,
	M1OverviewResponse,
	M1RollingTotalData,
	M1ShiftStatsData,
	M1UpcomingSectionData,
	ProductionChartData,
	UpcomingCampaignRowData,
} from "../models/M1Overview";
import { KPIData } from "@/shared/models/KpiData";
import type {
	ActiveDMHEvent,
	RollChangeCountdownData,
} from "@/pages/mill/overview-2/models/MillOverview2";

const moment = getMomentTz();

export const getM1Overview = async (
	signal?: AbortSignal,
): Promise<M1OverviewResponse> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		"[M1].[GetMillOverview]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const tables = resp.data.tables || [];

		// Result Set 1: Rolling Total Data
		const rollingTotalRow = tables[0]?.rows?.[0] || {};
		const rollingTotal: M1RollingTotalData = {
			BilletTonsCharged: rollingTotalRow.BilletTonsCharged ?? 0,
			BilletTonsChargedScheduled:
				rollingTotalRow.BilletTonsChargedScheduled ?? 0,
			BilletPcsCharged: rollingTotalRow.BilletPcsCharged ?? 0,
			BilletPcsChargedScheduled: rollingTotalRow.BilletPcsChargedScheduled ?? 0,
			RolledTons: rollingTotalRow.RolledTons ?? 0,
			RolledTonsScheduled: rollingTotalRow.RolledTonsScheduled ?? 0,
			FinishedTons: rollingTotalRow.FinishedTons ?? 0,
			FinishedTonsScheduled: rollingTotalRow.FinishedTonsScheduled ?? 0,
			FinishedBundles: (rollingTotalRow.FinishedBundles as number) ?? null,
			MillYield: rollingTotalRow.MillYield ?? 0,
			MillYieldScheduled: rollingTotalRow.MillYieldScheduled ?? 0,
			FinishedYield: rollingTotalRow.FinishedYield ?? 0,
			FinishedYieldScheduled: rollingTotalRow.FinishedYieldScheduled ?? 0,
			TotalCobbles: rollingTotalRow.TotalCobbles ?? 0,
			Production: rollingTotalRow.Production ?? 0,
			Delays: rollingTotalRow.Delays ?? 0,
			GapTime: rollingTotalRow.GapTime ?? 0,
			HoursActual: rollingTotalRow.HoursActual ?? 0,
			HoursScheduled: rollingTotalRow.HoursScheduled ?? 0,
			TPHActual: rollingTotalRow.TPHActual ?? 0,
			TPHBudgeted: rollingTotalRow.TPHBudgeted ?? 0,
			TPHTargeted: rollingTotalRow.TPHTargeted ?? null,
		};

		// Result Set 2: Current Shift Stats (no TotalAdjustments)
		const currentShiftRow = tables[1]?.rows?.[0] || {};
		const currentShift: M1ShiftStatsData = {
			ShiftId: currentShiftRow.ShiftId ?? 0,
			ShiftName: currentShiftRow.ShiftName ?? "Current Shift",
			BilletTons: currentShiftRow.BilletTons ?? 0,
			BilletPcs: currentShiftRow.BilletPcs ?? 0,
			TotalCobbles: currentShiftRow.TotalCobbles ?? 0,
			Production: currentShiftRow.Production ?? 0,
			Delays: currentShiftRow.Delays ?? 0,
			GapTime: currentShiftRow.GapTime ?? 0,
			TPH: currentShiftRow.TPH ?? 0,
			TargetTPH: currentShiftRow.TargetTPH ?? null,
			TPHDifference: currentShiftRow.TPHDifference ?? 0,
		};

		// Result Set 3: Previous Shift Stats (no TotalAdjustments)
		const previousShiftRow = tables[2]?.rows?.[0] || {};
		const previousShift: M1ShiftStatsData = {
			ShiftId: previousShiftRow.ShiftId ?? 0,
			ShiftName: previousShiftRow.ShiftName ?? "Previous Shift",
			BilletTons: previousShiftRow.BilletTons ?? 0,
			BilletPcs: previousShiftRow.BilletPcs ?? 0,
			TotalCobbles: previousShiftRow.TotalCobbles ?? 0,
			Production: previousShiftRow.Production ?? 0,
			Delays: previousShiftRow.Delays ?? 0,
			GapTime: previousShiftRow.GapTime ?? 0,
			TPH: previousShiftRow.TPH ?? 0,
			TargetTPH: previousShiftRow.TargetTPH ?? null,
			TPHDifference: previousShiftRow.TPHDifference ?? 0,
		};

		// Result Set 4: Current Section Data (Previous, Current, Next campaigns)
		const currentSectionRows = tables[3]?.rows || [];
		const mapCampaignRow = (row: Record<string, unknown>): CampaignRowData => ({
			SectionId: (row.SectionId as number) ?? null,
			SectionName: (row.SectionName as string) ?? null,
			HeatNumber: (row.HeatNumber as string) ?? null,
			Grade: (row.Grade as string) ?? null,
			ChargeTons: (row.ChargeTons as number) ?? null,
			ChargeBilletsQty: (row.ChargeBilletsQty as number) ?? null,
			RolledTons: (row.RolledTons as number) ?? null,
			RolledBillets: (row.RolledBillets as number) ?? null,
			TPH: (row.TPH as number) ?? null,
			Hours: (row.Hours as number) ?? null,
		});

		const previousRow = currentSectionRows.find(
			(r: Record<string, unknown>) => r.RowType === "Previous",
		);
		const currentRow = currentSectionRows.find(
			(r: Record<string, unknown>) => r.RowType === "Current",
		);
		const nextRow = currentSectionRows.find(
			(r: Record<string, unknown>) => r.RowType === "Next",
		);
		const firstCurrentRow = currentSectionRows[0] || {};

		const currentSection: M1CurrentSectionData = {
			CurrentSectionName: (firstCurrentRow.CurrentSectionName as string) ?? "",
			Previous: previousRow ? mapCampaignRow(previousRow) : null,
			Current: currentRow ? mapCampaignRow(currentRow) : null,
			Next: nextRow ? mapCampaignRow(nextRow) : null,
			ProjectedRollOut: firstCurrentRow.ProjectedRollOut
				? moment.utc(firstCurrentRow.ProjectedRollOut as string).toDate()
				: null,
			RollChangeTime: (firstCurrentRow.RollChangeTime as number) ?? null,
			RollChangeScheduled:
				(firstCurrentRow.RollChangeScheduled as number) ?? null,
			ScheduledHours: (firstCurrentRow.ScheduledHours as number) ?? null,
			CurrentHours: (firstCurrentRow.CurrentHours as number) ?? null,
			RemainingHours: (firstCurrentRow.RemainingHours as number) ?? null,
			VarianceHours: (firstCurrentRow.VarianceHours as number) ?? null,
			EventStart: firstCurrentRow.EventStart
				? moment.utc(firstCurrentRow.EventStart as string).toDate()
				: null,
			ScheduledEnd: firstCurrentRow.ScheduledEnd
				? moment.utc(firstCurrentRow.ScheduledEnd as string).toDate()
				: null,
		};

		// Result Set 5: Upcoming Section Data
		const upcomingSectionRows = tables[4]?.rows || [];
		const mapUpcomingRow = (
			row: Record<string, unknown>,
		): UpcomingCampaignRowData => ({
			SectionId: (row.SectionId as number) ?? 0,
			SectionName: (row.SectionName as string) ?? "",
			ScheduledStart: row.ScheduledStart
				? moment.utc(row.ScheduledStart).toDate()
				: null,
			ChargeTons: (row.ChargeTons as number) ?? 0,
			ChargeBilletsQty: (row.ChargeBilletsQty as number) ?? 0,
			BudgetTPH: (row.BudgetTPH as number) ?? 0,
			Hours: (row.Hours as number) ?? 0,
			TargetTPH: row.TargetTPH != null ? (row.TargetTPH as number) : null,
		});

		const firstUpcoming = upcomingSectionRows.find(
			(r: Record<string, unknown>) => r.RowType === "First",
		);
		const secondUpcoming = upcomingSectionRows.find(
			(r: Record<string, unknown>) => r.RowType === "Second",
		);
		const thirdUpcoming = upcomingSectionRows.find(
			(r: Record<string, unknown>) => r.RowType === "Third",
		);
		const firstUpcomingRow = upcomingSectionRows[0] || {};

		const upcomingSection: M1UpcomingSectionData = {
			UpcomingSectionName:
				(firstUpcomingRow.UpcomingSectionName as string) ?? "",
			First: firstUpcoming ? mapUpcomingRow(firstUpcoming) : null,
			Second: secondUpcoming ? mapUpcomingRow(secondUpcoming) : null,
			Third: thirdUpcoming ? mapUpcomingRow(thirdUpcoming) : null,
			ProjectedBreakin: firstUpcomingRow.ProjectedBreakin
				? moment.utc(firstUpcomingRow.ProjectedBreakin as string).toDate()
				: null,
			RollChangeTime:
				firstUpcomingRow.RollChangeTime != null
					? (firstUpcomingRow.RollChangeTime as number)
					: null,
		};

		// Result Set 6: Stand Readiness — empty for M1 (no carriers/rollsets)
		const standReadiness: [] = [];

		// Result Set 7: Usage Meters — placeholder zeros
		const _usageMetersRow = tables[6]?.rows?.[0] || {};
		const usageMeters = {
			WaterUsage: _usageMetersRow.WaterUsage ?? 0,
			WaterTarget: _usageMetersRow.WaterTarget ?? 0,
			ElectricityUsage: _usageMetersRow.ElectricityUsage ?? 0,
			ElectricityTarget: _usageMetersRow.ElectricityTarget ?? 0,
			GasUsage: _usageMetersRow.GasUsage ?? 0,
			GasTarget: _usageMetersRow.GasTarget ?? 0,
		};

		// Result Set 8: Current Shift KPI Data
		const currentShiftKPIRow = tables[7]?.rows?.[0] || {};
		const currentShiftKPI: KPIData = {
			TPH: currentShiftKPIRow.TPH ?? 0,
			TPHActualAvg: currentShiftKPIRow.TPHActualAvg ?? 0,
			TPHBudget: currentShiftKPIRow.TPHBudget ?? 0,
			TPHTarget: currentShiftKPIRow.TPHTarget ?? null,
			TPHAvgLastHour: currentShiftKPIRow.TPHAvgLastHour ?? 0,
			Uptime: currentShiftKPIRow.Uptime ?? 0,
			UptimeMillsOn: currentShiftKPIRow.UptimeMillsOn ?? 0,
			HoursAvailable: currentShiftKPIRow.HoursAvailable ?? 0,
			UptimePercentage: currentShiftKPIRow.UptimePercentage ?? 0,
			AvgTPHDuringUptime: currentShiftKPIRow.AvgTPHDuringUptime ?? 0,
			PushRateActual: currentShiftKPIRow.PushRateActual ?? 0,
			PushRateWithDelays: currentShiftKPIRow.PushRateWithDelays ?? 0,
			PushRateScheduled: currentShiftKPIRow.PushRateScheduled ?? 0,
			Productivity: currentShiftKPIRow.Productivity ?? null,
			ProductivityOverScheduled:
				currentShiftKPIRow.ProductivityOverScheduled ?? 0,
			TPHPct: currentShiftKPIRow.TPHPct ?? 0,
			UptimePct: currentShiftKPIRow.UptimePct ?? 0,
			PushRatePct: currentShiftKPIRow.PushRatePct ?? 0,
			TPHRed: currentShiftKPIRow.TPHRed ?? 0,
			TPHOrange: currentShiftKPIRow.TPHOrange ?? 0,
			TPHYellow: currentShiftKPIRow.TPHYellow ?? 0,
			TPHGreenSoft: currentShiftKPIRow.TPHGreenSoft ?? 0,
			TPHGreenNormal: currentShiftKPIRow.TPHGreenNormal ?? 0,
			TPHGreenHard: currentShiftKPIRow.TPHGreenHard ?? 0,
			UptimeRed: currentShiftKPIRow.UptimeRed ?? 0,
			UptimeOrange: currentShiftKPIRow.UptimeOrange ?? 0,
			UptimeYellow: currentShiftKPIRow.UptimeYellow ?? 0,
			UptimeGreenSoft: currentShiftKPIRow.UptimeGreenSoft ?? 0,
			UptimeGreenNormal: currentShiftKPIRow.UptimeGreenNormal ?? 0,
			UptimeGreenHard: currentShiftKPIRow.UptimeGreenHard ?? 0,
			PushRateRed: currentShiftKPIRow.PushRateRed ?? 0,
			PushRateOrange: currentShiftKPIRow.PushRateOrange ?? 0,
			PushRateYellow: currentShiftKPIRow.PushRateYellow ?? 0,
			PushRateGreenSoft: currentShiftKPIRow.PushRateGreenSoft ?? 0,
			PushRateGreenNormal: currentShiftKPIRow.PushRateGreenNormal ?? 0,
			PushRateGreenHard: currentShiftKPIRow.PushRateGreenHard ?? 0,
			ProductivityRed: currentShiftKPIRow.ProductivityRed ?? 0,
			ProductivityOrange: currentShiftKPIRow.ProductivityOrange ?? 0,
			ProductivityYellow: currentShiftKPIRow.ProductivityYellow ?? 0,
			ProductivityGreenSoft: currentShiftKPIRow.ProductivityGreenSoft ?? 0,
			ProductivityGreenNormal: currentShiftKPIRow.ProductivityGreenNormal ?? 0,
			ProductivityGreenHard: currentShiftKPIRow.ProductivityGreenHard ?? 0,
		};

		// Result Set 9: Rolling Total KPI Data
		const rollingTotalKPIRow = tables[8]?.rows?.[0] || {};
		const rollingTotalKPI: KPIData = {
			TPH: rollingTotalKPIRow.TPH ?? 0,
			TPHActualAvg: rollingTotalKPIRow.TPHActualAvg ?? 0,
			TPHBudget: rollingTotalKPIRow.TPHBudget ?? 0,
			TPHTarget: rollingTotalKPIRow.TPHTarget ?? null,
			TPHAvgLastHour: rollingTotalKPIRow.TPHAvgLastHour ?? 0,
			Uptime: rollingTotalKPIRow.Uptime ?? 0,
			UptimeMillsOn: rollingTotalKPIRow.UptimeMillsOn ?? 0,
			HoursAvailable: rollingTotalKPIRow.HoursAvailable ?? 0,
			UptimePercentage: rollingTotalKPIRow.UptimePercentage ?? 0,
			AvgTPHDuringUptime: rollingTotalKPIRow.AvgTPHDuringUptime ?? 0,
			PushRateActual: rollingTotalKPIRow.PushRateActual ?? 0,
			PushRateWithDelays: rollingTotalKPIRow.PushRateWithDelays ?? 0,
			PushRateScheduled: rollingTotalKPIRow.PushRateScheduled ?? 0,
			Productivity: rollingTotalKPIRow.Productivity ?? null,
			ProductivityOverScheduled:
				rollingTotalKPIRow.ProductivityOverScheduled ?? 0,
			TPHPct: rollingTotalKPIRow.TPHPct ?? 0,
			UptimePct: rollingTotalKPIRow.UptimePct ?? 0,
			PushRatePct: rollingTotalKPIRow.PushRatePct ?? 0,
			TPHRed: rollingTotalKPIRow.TPHRed ?? 0,
			TPHOrange: rollingTotalKPIRow.TPHOrange ?? 0,
			TPHYellow: rollingTotalKPIRow.TPHYellow ?? 0,
			TPHGreenSoft: rollingTotalKPIRow.TPHGreenSoft ?? 0,
			TPHGreenNormal: rollingTotalKPIRow.TPHGreenNormal ?? 0,
			TPHGreenHard: rollingTotalKPIRow.TPHGreenHard ?? 0,
			UptimeRed: rollingTotalKPIRow.UptimeRed ?? 0,
			UptimeOrange: rollingTotalKPIRow.UptimeOrange ?? 0,
			UptimeYellow: rollingTotalKPIRow.UptimeYellow ?? 0,
			UptimeGreenSoft: rollingTotalKPIRow.UptimeGreenSoft ?? 0,
			UptimeGreenNormal: rollingTotalKPIRow.UptimeGreenNormal ?? 0,
			UptimeGreenHard: rollingTotalKPIRow.UptimeGreenHard ?? 0,
			PushRateRed: rollingTotalKPIRow.PushRateRed ?? 0,
			PushRateOrange: rollingTotalKPIRow.PushRateOrange ?? 0,
			PushRateYellow: rollingTotalKPIRow.PushRateYellow ?? 0,
			PushRateGreenSoft: rollingTotalKPIRow.PushRateGreenSoft ?? 0,
			PushRateGreenNormal: rollingTotalKPIRow.PushRateGreenNormal ?? 0,
			PushRateGreenHard: rollingTotalKPIRow.PushRateGreenHard ?? 0,
			ProductivityRed: rollingTotalKPIRow.ProductivityRed ?? 0,
			ProductivityOrange: rollingTotalKPIRow.ProductivityOrange ?? 0,
			ProductivityYellow: rollingTotalKPIRow.ProductivityYellow ?? 0,
			ProductivityGreenSoft: rollingTotalKPIRow.ProductivityGreenSoft ?? 0,
			ProductivityGreenNormal: rollingTotalKPIRow.ProductivityGreenNormal ?? 0,
			ProductivityGreenHard: rollingTotalKPIRow.ProductivityGreenHard ?? 0,
		};

		// Result Set 10: Production Chart (Hourly)
		const productionChart: ProductionChartData[] = (tables[9]?.rows || []).map(
			(row: Record<string, unknown>) => ({
				Time: row.Time ? moment.utc(row.Time).toDate() : null,
				TargetTPH: row.TargetTPH != null ? (row.TargetTPH as number) : null,
				BudgetTPH: (row.BudgetTPH as number) ?? 0,
				Actual: row.Actual != null ? (row.Actual as number) : null,
				CumulativeAvg:
					row.CumulativeAvg != null ? (row.CumulativeAvg as number) : null,
			}),
		);

		// Result Set 11: Is In Delay
		const isInDelayRow = tables[10]?.rows?.[0] || {};
		const isInDelay = Boolean(isInDelayRow.IsInDelay);
		const currentDelayId = (isInDelayRow.DelayId as number | null) ?? null;

		// Result Set 12: Roll Change Countdown Data
		const rcRow = tables[11]?.rows?.[0];

		// Result Set 13: Active D/M/H Events (multiple rows)
		const activeDmhEvents: ActiveDMHEvent[] = (tables[12]?.rows ?? []).map(
			(row: Record<string, unknown>) => ({
				EventTypeCode: (row.EventTypeCode as string) ?? "",
				Start: moment.utc(row.Start as string).toDate(),
				End: row.End ? moment.utc(row.End as string).toDate() : null,
				RemainingMinutes:
					row.RemainingMinutes != null
						? (row.RemainingMinutes as number)
						: null,
			}),
		);

		const rollChangeCountdown: RollChangeCountdownData | null = rcRow
			? {
					StatusCode: (rcRow.StatusCode as string) ?? "",
					FirstBilletTimestamp: rcRow.FirstBilletTimestamp
						? moment.utc(rcRow.FirstBilletTimestamp).toDate()
						: null,
					ScheduledRollChangeHours:
						(rcRow.ScheduledRollChangeHours as number) ?? 0,
					RemainingMinutes: (rcRow.RemainingMinutes as number) ?? null,
					IsPaused: Boolean(rcRow.IsPaused),
					WarningThresholdMinutes:
						(rcRow.WarningThresholdMinutes as number) ?? 10,
					ActiveDMHEvents: activeDmhEvents,
				}
			: null;

		return {
			RollingTotal: rollingTotal,
			CurrentShift: currentShift,
			PreviousShift: previousShift,
			CurrentSection: currentSection,
			UpcomingSection: upcomingSection,
			StandReadiness: standReadiness,
			UsageMeters: usageMeters,
			CurrentShiftKPI: currentShiftKPI,
			RollingTotalKPI: rollingTotalKPI,
			ProductionChart: productionChart,
			IsInDelay: isInDelay,
			CurrentDelayId: currentDelayId,
			RollChangeCountdown: rollChangeCountdown,
		};
	} else {
		throw new Error(resp.message || "Error fetching M1 overview data");
	}
};
